import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { visibleNavFor, effectiveRolesOf } from "@/lib/nav";
import BuildInfo from "@/components/BuildInfo";
import {
  Menu,
  X,
  Tablet,
  Home,
  LogOut,
} from "lucide-react";

// Shared top chrome for the PWA. Light header with hamburger + brand + PWA glyph.
// Renders a slide-out drawer for the hamburger menu.
export default function MobileShell({ children, dark = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState(null);

  // Same module list the desktop sidebar uses, so the PWA drawer shows everything
  // this user has access to (not just a hard-coded shortlist).
  useEffect(() => {
    let cancelled = false;
    api.get("/hospital/modules")
      .then(({ data }) => { if (!cancelled) setModules(data?.modules || {}); })
      .catch(() => { if (!cancelled) setModules({}); });
    return () => { cancelled = true; };
  }, [user?.active_hospital_id]);

  // Reception's mobile home is their own queue view, not the doctor's.
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role];
  const mobileHome = (!roles.includes("doctor") && roles.includes("reception"))
    ? "/reception-mobile"
    : "/doctor-mobile";

  const navItems = visibleNavFor({
    effectiveRoles: effectiveRolesOf(user),
    modules,
    moduleOverrides: user?.module_overrides || {},
    mobile: true,   // route to the touch views, not the desktop pages
  });

  const hospitalName =
    user?.active_hospital_name || user?.hospital_name || "eMedbay";
  const logoUrl = user?.branding?.logo_url || "";

  const doLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className={dark ? "min-h-screen bg-slate-950 text-white" : "min-h-screen bg-slate-50 text-slate-900"} data-testid="mobile-shell">
      {/* === Top chrome (always light) === */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setOpen(true)}
            className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center active:scale-95 transition"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={20} className="text-slate-700" />
          </button>

          <div className="flex-1 text-center px-2 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-7 mx-auto object-contain" />
            ) : (
              <div className="text-xl font-extrabold text-slate-900 leading-tight truncate" style={{ fontFamily: "Work Sans" }}>
                eMedbay
              </div>
            )}
            <div className="text-xs text-slate-500 truncate" data-testid="mobile-hospital-name">
              {hospitalName}
            </div>
          </div>

          <div
            className="w-11 h-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center"
            aria-label="Mobile app"
            data-testid="mobile-pwa-badge"
          >
            <Tablet size={18} />
          </div>
        </div>
      </header>

      {/* === Content === */}
      <main className="pb-8">{children}</main>

      {/* === Drawer === */}
      {open ? (
        <div className="fixed inset-0 z-50" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white text-slate-900 shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="min-w-0">
                <div className="text-lg font-extrabold" style={{ fontFamily: "Work Sans" }}>
                  {user?.name || "Doctor"}
                </div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="p-3 space-y-1 flex-1 overflow-auto" data-testid="drawer-nav">
              {/* Mobile home first, then every module this user can access. */}
              <DrawerItem
                icon={Home}
                label="Mobile Dashboard"
                onClick={() => { setOpen(false); nav(mobileHome); }}
                testid="drawer-home"
              />

              {navItems.length ? (
                <div className="pt-2 mt-2 border-t border-slate-200">
                  <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Modules</div>
                  {navItems.map((item) => (
                    <DrawerItem
                      key={item.to}
                      icon={item.icon}
                      label={item.label}
                      onClick={() => { setOpen(false); nav(item.to); }}
                      testid={`drawer-${item.testid}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="px-3 py-4 text-sm text-slate-400">Loading modules…</div>
              )}
            </nav>

            <div className="px-3 pt-3 border-t border-slate-200">
              <BuildInfo compact />
            </div>

            <div className="p-3">
              <button
                onClick={doLogout}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-700 hover:bg-red-50"
                data-testid="drawer-logout"
              >
                <LogOut size={18} />
                <span className="font-medium">Sign out</span>
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function DrawerItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-slate-700 hover:bg-slate-100 transition"
      data-testid={testid}
    >
      <Icon size={18} />
      <span className="font-medium">{label}</span>
    </button>
  );
}
